import React, { useState } from 'react';
import { 
  CpuChipIcon, 
  ServerIcon, 
  ArrowPathIcon,
  DocumentTextIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { useServerAPI, useServerMutation } from '../hooks/useServerData';
import { useServer } from '../contexts/ServerContext';

const System = () => {
  const [selectedLogType, setSelectedLogType] = useState('system');
  const [logLines, setLogLines] = useState(50);
  const { selectedServer, getCurrentServer, switchingServer } = useServer();

  // Using new server-aware hooks
  const { data: systemStatus, loading: systemLoading, error: systemError } = useServerAPI(
    '/api/system/status',
    { 
      refetchInterval: 10000,
      onError: (error) => console.error('Erro ao buscar status do sistema:', error)
    }
  );

  const { data: processes, loading: processesLoading } = useServerAPI(
    '/api/system/processes?sortBy=cpu',
    { refetchInterval: 15000 }
  );

  const { data: dockerStatus, loading: dockerLoading } = useServerAPI(
    '/api/server/docker/status',
    { refetchInterval: 30000 }
  );

  const { data: logs, loading: logsLoading, refresh: refreshLogs } = useServerAPI(
    `/api/servers/${selectedServer}/logs/${selectedLogType}?lines=${logLines}`,
    { 
      refetchInterval: 30000,
      dependencies: [selectedLogType, logLines]
    }
  );

  const { mutate: restartService, loading: restartLoading } = useServerMutation();

  const system = systemStatus;
  const processList = processes?.processes || [];
  const docker = dockerStatus;
  const systemLogs = logs?.logs || [];

  const handleRestartService = async (serviceName) => {
    if (window.confirm(`Tem certeza que deseja reiniciar o serviço ${serviceName}?`)) {
      try {
        await restartService('/api/server/docker/restart', { service: serviceName });
        alert('Serviço reiniciado com sucesso!');
        // Refresh docker status
        setTimeout(() => {
          window.location.reload(); // Simple refresh for now
        }, 2000);
      } catch (error) {
        alert(`Erro ao reiniciar serviço: ${error.message}`);
      }
    }
  };

  const MetricCard = ({ title, children, icon: Icon }) => (
    <div className="card">
      <div className="flex items-center mb-4">
        <Icon className="h-6 w-6 text-blue-600 mr-2" />
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
      </div>
      {children}
    </div>
  );

  const ProgressBar = ({ value, max, label, color = 'blue' }) => {
    const percentage = Math.min((value / max) * 100, 100);
    const colorClass = percentage > 80 ? 'bg-red-500' : 
                      percentage > 60 ? 'bg-yellow-500' : 
                      `bg-${color}-500`;

    return (
      <div className="mb-3">
        <div className="flex justify-between text-sm text-gray-600 mb-1">
          <span>{label}</span>
          <span>{percentage.toFixed(1)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all ${colorClass}`}
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Sistema</h1>
        <p className="mt-1 text-sm text-gray-500">
          Monitoramento e gerenciamento do servidor: <strong>{getCurrentServer()?.name}</strong>
        </p>
        {switchingServer && (
          <div className="mt-2 text-sm text-blue-600 flex items-center">
            <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" />
            Trocando servidor...
          </div>
        )}
      </div>

      {/* System Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MetricCard title="Recursos do Sistema" icon={CpuChipIcon}>
          {systemLoading ? (
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
            </div>
          ) : systemError ? (
            <div className="text-red-600 text-sm">
              Erro: {systemError}
            </div>
          ) : system ? (
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Hostname:</span>
                  <span className="font-medium">{system.hostname}</span>
                </div>
                
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Uptime:</span>
                  <span className="font-medium">{system.uptime}</span>
                </div>
              </div>

              {system.memory && (
                <ProgressBar
                  value={system.memory.used}
                  max={system.memory.total}
                  label={`Memória (${system.memory.used}MB / ${system.memory.total}MB)`}
                  color="blue"
                />
              )}

              {system.disk && (
                <ProgressBar
                  value={system.disk.usagePercent}
                  max={100}
                  label={`Disco (${system.disk.used} / ${system.disk.total})`}
                  color="green"
                />
              )}

              {system.loadAverage && (
                <div>
                  <p className="text-sm text-gray-600 mb-2">Load Average:</p>
                  <div className="flex space-x-4 text-sm">
                    <span>1min: <strong>{system.loadAverage['1min']}</strong></span>
                    <span>5min: <strong>{system.loadAverage['5min']}</strong></span>
                    <span>15min: <strong>{system.loadAverage['15min']}</strong></span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
            </div>
          )}
        </MetricCard>

        <MetricCard title="Serviços Docker" icon={ServerIcon}>
          {docker?.services ? (
            <div className="space-y-3">
              <div className="text-sm text-gray-600">
                <span className="font-medium">{docker.services.length}</span> containers em execução
              </div>
              
              <div className="max-h-64 overflow-y-auto space-y-2">
                {docker.services.map((service, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{service.name}</p>
                      <p className="text-xs text-gray-500">{service.status}</p>
                    </div>
                    <button
                      onClick={() => handleRestartService(service.name)}
                      disabled={restartLoading}
                      className="btn-secondary text-xs py-1 px-2"
                    >
                      <ArrowPathIcon className={`h-3 w-3 ${restartLoading ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-16 bg-gray-200 rounded"></div>
            </div>
          )}
        </MetricCard>
      </div>

      {/* Processes */}
      <MetricCard title="Processos (Top 10 por CPU)" icon={ChartBarIcon}>
        {processList.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">PID</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">CPU%</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">MEM%</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Comando</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {processList.slice(0, 10).map((process, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm text-gray-900">{process.pid}</td>
                    <td className="px-3 py-2 text-sm text-gray-500">{process.user}</td>
                    <td className="px-3 py-2 text-sm text-gray-900">
                      <span className={`font-medium ${process.cpu > 50 ? 'text-red-600' : 'text-green-600'}`}>
                        {process.cpu.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900">{process.memory.toFixed(1)}%</td>
                    <td className="px-3 py-2 text-sm text-gray-500 truncate max-w-xs">
                      {process.command}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded mb-2"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        )}
      </MetricCard>

      {/* System Logs */}
      <MetricCard title="Logs do Sistema" icon={DocumentTextIcon}>
        <div className="space-y-4">
          {/* Log Controls */}
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex space-x-2">
              {['system', 'backup', 'auth'].map((type) => (
                <button
                  key={type}
                  onClick={() => setSelectedLogType(type)}
                  className={`px-3 py-1 rounded text-sm font-medium ${
                    selectedLogType === type
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>

            <select
              value={logLines}
              onChange={(e) => setLogLines(Number(e.target.value))}
              className="input text-sm"
            >
              <option value={25}>25 linhas</option>
              <option value={50}>50 linhas</option>
              <option value={100}>100 linhas</option>
              <option value={200}>200 linhas</option>
            </select>
          </div>

          {/* Log Content */}
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-xs max-h-96 overflow-y-auto">
            {systemLogs.length > 0 ? (
              systemLogs.map((line, index) => (
                <div key={index} className="mb-1">
                  {line}
                </div>
              ))
            ) : (
              <div className="text-gray-400">
                Nenhum log encontrado para {selectedLogType}
              </div>
            )}
          </div>
        </div>
      </MetricCard>
    </div>
  );
};

export default System;